import Vue from 'vue';

Vue.component('new-input-component', {
    props: {
        inputId: {
            type: String,
        },
        labelId: {
            type: String,
        },
        errors: {
            type: Object,
        },
        isTextArea: {
            type: Boolean,
            default: false,
        },
        isBrandstoreSearchInput: {
            type: Boolean,
            default: false,
        },
        dropdownValue: {
            type: String,
            required: false,
            default: '',
        },
        handleDropdownToggle: {
            type: Function,
            required: false,
        },
        showErrorForId: {
            type: String,
            required: false,
        },
        showErrorForValue: {
            type: String,
            required: false,
        },
        customErrorId: {
            type: String,
            required: false,
            default: null,
        },
        customErrorCheck: {
            type: String,
            required: false,
            default: null,
        },
    },

    data() {
        return {
            input: null,
            inputContainer: null,
            inputValue: this.dropdownValue || '',
            timer: null,
            isErrorVisible: false,
        };
    },

    mounted() {
        const isDropdownValueSet =
            this.dropdownValue && this.dropdownValue.length > 0;
        this.input = this.$el.querySelector(`#${this.inputId}`);
        this.inputContainer = this.$el.querySelector(
            `#container-${this.inputId}`,
        );
        this.handleInputChange();
        if (isDropdownValueSet) {
            this.handleInputLabelMoved();
        }

        EventBus.$on('brandstore-dropdown-value-selected', (value) => {
            if (this.inputId === value.inputId) {
                let parsedStore = JSON.parse(value.store);
                this.inputValue = parsedStore.title;
            }
        });
    },

    unmounted() {
        clearTimeout(this.timer);
    },

    watch: {
        dropdownValue() {
            this.handleInputLabelMoved();
        },

        inputValue(value) {
            if (this.isBrandstoreSearchInput) {
                this.$parent.store = value;
            }
            this.handleInputLabelMoved();
        },

        errors: {
            handler: function (newErrors) {
                const errs = newErrors.errors;

                // 1. Custom error takes priority
                if (this.customErrorId && this.customErrorCheck) {
                    const customList = errs[this.customErrorId] || [];
                    this.isErrorVisible = customList.some((e) =>
                        e.includes(this.customErrorCheck),
                    );
                    return;
                }

                // 2. Brandstore‐search special case
                if (
                    (errs.dealerCode || errs.store) &&
                    this.inputId === 'searchBrandstore'
                ) {
                    this.isErrorVisible = true;
                    return;
                }

                // 3. General case: no errors for this input (or override id) => hide
                const overrideId = this.showErrorForId || this.inputId;
                if (!errs[this.inputId] && !errs[overrideId]) {
                    this.isErrorVisible = false;
                    return;
                }

                // 4. Otherwise show error
                this.isErrorVisible = true;
            },
            deep: true,
        },
    },

    computed: {
        handleCheckDropdownSelected() {
            if (this.dropdownValue && this.dropdownValue.length > 0) {
                this.isErrorVisible = false;
                return this.handleHiddenError();
            }
        },

        handleShowContainerError() {
            return this.isErrorVisible ? 'new-input__container--error' : '';
        },

        handleShowEmailFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('email', 'valid email address');
        },

        handleShowEmailEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('email', 'blank');
        },

        handleShowFirstNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('firstName', 'blank');
        },

        handleShowLastNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('lastName', 'blank');
        },

        handleShowZipCodeEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('postalCode', 'blank');
        },

        handleShowDealerEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();

            if (Boolean(this.$parent.store)) return this.handleHiddenError();

            if (!this.inputId.includes('store'))
                return this.handleHiddenError();

            const hasError = this.errors.errors.hasOwnProperty('store');

            if (!hasError) return this.handleHiddenError();

            const isError = this.errors.errors['store'][0].includes('blank');

            if (!isError) return this.handleHiddenError();
        },

        handleShowPhoneNumberEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('phone', 'blank');
        },

        handleShowPhoneNumberFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();

            const errors = [
                this.handleErrorChecks('phone', 'phone number format'),
                this.handleErrorChecks('phone', 'must be an integer'),
                this.handleErrorChecks(
                    'phone',
                    'does not seem to be a valid phone number',
                ),
            ];

            if (errors.includes(undefined)) return;

            return this.handleHiddenError();
        },

        handleShowLicensePlateFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('licensePlate', 'is invalid');
        },

        handleShowHouseNumberEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('houseNumber', 'blank');
        },

        handleShowHouseNumberFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('houseNumber', 'must be an integer');
        },

        handleHeardFromEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('heardFrom', 'blank');
        },

        handleSiftingQuestionEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('siftingQuestion', 'blank');
        },

        handleInvoiceNumberEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('invoiceNumber', 'blank');
        },

        handleMaintenanceDateEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('maintenanceDate', 'blank');
        },

        handleShowCityNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('city', 'blank');
        },

        handleShowCompanyEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('company', 'blank');
        },

        handleShowStreetNameEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('streetName', 'blank');
        },

        handleShowMessageEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('message', 'blank');
        },

        handleShowActivityLocationEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('activityLocation', 'blank');
        },

        handleShowEmptyStoreError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('store', 'blank');
        },

        handleFinancingTypeEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('financingType', 'blank');
        },

        handleShowEmptyBrandstoreError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            if (this.showErrorForId && this.showErrorForValue) {
                return this.handleErrorCustomInputChecks(
                    this.showErrorForValue,
                    this.showErrorForId,
                );
            }
            return this.handleErrorChecks('brandStore', 'blank');
        },

        handleShowCountryEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('country', 'blank');
        },

        handleShowEmptyTopicError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('subject', 'blank');
        },

        handleShowVinEmptyError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('vin', 'blank');
        },

        handleShowCvFileSizeTooLargeError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('cv', 'File size too large');
        },

        handleShowCvFileSizeTooSmallError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('cv', 'File size too small');
        },

        handleShowFileExtensionError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('cv', 'Extension not allowed');
        },

        handleShowEmailConsentRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('emailConsent', 'blank');
        },

        handleShowSmsConsentRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('smsConsent', 'blank');
        },

        handleShowPhoneConsentRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('phoneConsent', 'blank');
        },

        handleShowStoreCountryRequiredError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('storeCountry', 'blank');
        },

        handleShowSirenSiretWrongFormatError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('sirenSiret', 'invalid format');
        },

        handleShowOwnerManualError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return this.handleHiddenError();
            return this.handleErrorChecks('vin', 'owner-manual-error');
        },

        handleShowCustomError() {
            return this.handleErrorCustomInputChecks(
                this.customErrorCheck,
                this.customErrorId,
            );
        },
    },

    methods: {
        handleShowDownPaymentError() {
            // If custom error is active, hide this specific error
            if (this.customErrorId) return false;
            return (
                !this.$parent.isFormVisible &&
                this.errors.errors.hasOwnProperty('downPayment')
            );
        },

        handleHiddenError() {
            return 'new-input__error--hidden';
        },

        handleErrorCustomInputChecks(errorMessage, customErrorKey = null) {
            // If custom error props are not set, hide this
            if (!this.customErrorId || !this.customErrorCheck)
                return this.handleHiddenError();

            const inputId = customErrorKey || this.inputId;

            const hasError = this.errors.errors.hasOwnProperty(inputId);

            if (!hasError) return this.handleHiddenError();

            const isError = this.errors.errors[inputId].find((error) =>
                error.includes(errorMessage),
            );

            if (!isError) return this.handleHiddenError();

            // Return empty string if error should be shown (removes the hidden class)
            return '';
        },

        handleErrorChecks(id, errorMessage, customErrorKey = null) {
            // This check is now redundant here because individual computed props handle the customErrorId check
            // but kept for potential direct calls if any exist. Best practice would be to rely on computed props.
            if (this.customErrorId) return this.handleHiddenError();

            if (!this.inputId.includes(id)) return this.handleHiddenError();

            const errorKeyToCheck = customErrorKey || this.inputId;
            const hasError = this.errors.errors.hasOwnProperty(errorKeyToCheck);

            if (!hasError) return this.handleHiddenError();

            const isError = this.errors.errors[errorKeyToCheck].find((error) =>
                error.includes(errorMessage),
            );

            if (!isError) return this.handleHiddenError();

            // Return empty string if error should be shown (removes the hidden class)
            return '';
        },

        handleInputChange() {
            if (this.handleDropdownToggle) return;
            this.timer = setTimeout(() => {
                this.inputValue = this.input.value;
            }, 100);
        },

        handleInputLabelMoved() {
            const label = this.$el.querySelector(`#${this.labelId}`);

            if (!label) return;

            const isLabelBlack = label.classList.contains(
                'new-input__label-black',
            );
            if (isLabelBlack) return;

            const isInputEmpty = this.inputValue?.length === 0;

            const isDropdownEmpty = this.dropdownValue?.length === 0;

            if (isInputEmpty && isDropdownEmpty) {
                this.handleRemoveLabelClass(label);
                return;
            }

            this.handleAddLabelClass(label);
        },

        handleAddLabelClass(label) {
            if (this.isTextArea) {
                label.classList.add('new-input__textarea-label--moved');
                return;
            }

            label.classList.add('new-input__label--moved');
        },

        handleRemoveLabelClass(label) {
            if (this.isTextArea) {
                label.classList.remove('new-input__textarea-label--moved');
                return;
            }

            label.classList.remove('new-input__label--moved');
        },
    },
});
